import { artistTypes, authTypes } from '@/types';

export const validateUsername = (username: string) => {
  //"Username should contain only alphanumeric characters",
  const usernameRegex = /^[a-zA-Z0-9]+$/;
  return username.match(usernameRegex)
    ? ''
    : 'Username should contain only alphanumeric characters';
};

export const validateFirstName = (firstName: string) => {
  // Check if firstName is a non-empty string
  if (typeof firstName !== 'string' || firstName.trim().length === 0) {
    return 'First name must be a non-empty string';
  }
  return '';
};

export const validateLastName = (lastName: string) => {
  // Check if lastName is a non-empty string
  if (typeof lastName !== 'string' || lastName.trim().length === 0) {
    return 'Last name must be a non-empty string';
  }
  return '';
};

export const validatePhone = (phone: string) => {
  const phoneRegex = /^[0-9]{9}$/;
  return phone.match(phoneRegex) ? '' : 'Enter valid phone number';
};

export const validateCountry = (country: string) => {
  // Check if country is a non-empty string
  if (typeof country !== 'string' || country.trim().length === 0) {
    return 'Country must be a non-empty string';
  }
  return '';
};

export const validateNationalNo = (nationalNo: string) => {
  // must be 9 digits
  const nationalNoRegex = /^[0-9]{9}$/;
  return nationalNo.match(nationalNoRegex) ? '' : 'Enter valid national number';
};

export const validateStageName = (stageName: string) => {
  //not empty // Check if  is a non-empty string
  if (typeof stageName !== 'string' || stageName.trim().length === 0) {
    return 'Stage name must be a non-empty string';
  }
  return '';
};

export const validateDescription = (description: string) => {
  // Check if description is a non-empty string
  if (typeof description !== 'string' || description.trim().length === 0) {
    return 'Description must be a non-empty string';
  }
  return '';
};

export const validateBankName = (bankName: string) => {
  // Check if bankName is a non-empty string
  if (typeof bankName !== 'string' || bankName.trim().length === 0) {
    return 'Bank name must be a non-empty string';
  }
  return '';
};

// International bank account validation with IBAN support
export const validateAccountNo = (accountNo: string) => {
  if (!accountNo || typeof accountNo !== 'string') {
    return 'Account number is required';
  }

  const cleanedAccountNo = accountNo.replace(/\s/g, '').toUpperCase();

  // Check if it's an IBAN (International Bank Account Number)
  if (isIBAN(cleanedAccountNo)) {
    return validateIBAN(cleanedAccountNo);
  }

  // Check if it's a US routing number (9 digits)
  if (isUSRoutingNumber(cleanedAccountNo)) {
    return validateUSRoutingNumber(cleanedAccountNo);
  }

  // Check if it's a UK sort code + account number
  if (isUKBankAccount(cleanedAccountNo)) {
    return validateUKBankAccount(cleanedAccountNo);
  }

  // Generic validation for other formats
  return validateGenericAccountNumber(cleanedAccountNo);
};

// Helper functions for international bank account validation
const isIBAN = (accountNo: string): boolean =>
  /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(accountNo) && accountNo.length >= 15 && accountNo.length <= 34;

const validateIBAN = (iban: string): string => {
  // IBAN length validation by country
  const ibanLengths: { [key: string]: number } = {
    AD: 24,
    AE: 23,
    AL: 28,
    AT: 20,
    AZ: 28,
    BA: 20,
    BE: 16,
    BG: 22,
    BH: 22,
    BR: 29,
    BY: 28,
    CH: 21,
    CR: 22,
    CY: 28,
    CZ: 24,
    DE: 22,
    DK: 18,
    DO: 28,
    EE: 20,
    EG: 29,
    ES: 24,
    FI: 18,
    FO: 18,
    FR: 27,
    GB: 22,
    GE: 22,
    GI: 23,
    GL: 18,
    GR: 27,
    GT: 28,
    HR: 21,
    HU: 28,
    IE: 22,
    IL: 23,
    IS: 26,
    IT: 27,
    JO: 30,
    KW: 30,
    KZ: 20,
    LB: 28,
    LC: 32,
    LI: 21,
    LT: 20,
    LU: 20,
    LV: 21,
    MC: 27,
    MD: 24,
    ME: 22,
    MK: 19,
    MR: 27,
    MT: 31,
    MU: 30,
    NL: 18,
    NO: 15,
    PK: 24,
    PL: 28,
    PS: 29,
    PT: 25,
    QA: 29,
    RO: 24,
    RS: 22,
    SA: 24,
    SE: 24,
    SI: 19,
    SK: 24,
    SM: 27,
    TN: 24,
    TR: 26,
    UA: 29,
    VG: 24,
    XK: 20,
  };

  const countryCode = iban.substring(0, 2);
  const expectedLength = ibanLengths[countryCode];

  if (!expectedLength) {
    return 'Invalid country code in IBAN';
  }

  if (iban.length !== expectedLength) {
    return `IBAN length should be ${expectedLength} characters for ${countryCode}`;
  }

  // MOD-97-10 algorithm for IBAN validation
  if (!validateIBANChecksum(iban)) {
    return 'Invalid IBAN checksum';
  }

  return '';
};

const validateIBANChecksum = (iban: string): boolean => {
  // Move first 4 characters to end
  const rearranged = iban.substring(4) + iban.substring(0, 4);

  // Convert letters to numbers (A=10, B=11, ..., Z=35)
  let numericString = '';
  for (const char of rearranged) {
    if (/[A-Z]/.test(char)) {
      numericString += (char.charCodeAt(0) - 55).toString();
    } else {
      numericString += char;
    }
  }

  // Calculate mod 97
  let remainder = 0;
  for (const digit of numericString) {
    remainder = (remainder * 10 + parseInt(digit)) % 97;
  }

  return remainder === 1;
};

const isUSRoutingNumber = (accountNo: string): boolean => /^[0-9]{9}$/.test(accountNo);

const validateUSRoutingNumber = (routingNumber: string): string => {
  // US routing number checksum validation
  const digits = routingNumber.split('').map(Number);
  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += digits[i] * weights[i];
  }

  if (sum % 10 !== 0) {
    return 'Invalid US routing number checksum';
  }

  return '';
};

const isUKBankAccount = (accountNo: string): boolean =>
  /^[0-9]{14}$/.test(accountNo) || /^[0-9]{10}$/.test(accountNo);

const validateUKBankAccount = (accountNo: string): string => {
  if (accountNo.length === 14) {
    const sortCode = accountNo.substring(0, 6);
    const accountNumber = accountNo.substring(6);

    if (!/^[0-9]{6}$/.test(sortCode)) {
      return 'Invalid UK sort code format';
    }
    if (!/^[0-9]{8}$/.test(accountNumber)) {
      return 'Invalid UK account number format';
    }
  } else if (accountNo.length === 10) {
    if (!/^[0-9]{10}$/.test(accountNo)) {
      return 'Invalid UK account number format';
    }
  }

  return '';
};

const validateGenericAccountNumber = (accountNo: string): string => {
  const cleaned = accountNo.replace(/[^A-Z0-9]/gi, '');

  if (cleaned.length < 4) {
    return 'Account number must be at least 4 characters';
  }

  if (cleaned.length > 34) {
    return 'Account number must not exceed 34 characters';
  }

  if (!/[0-9]/.test(cleaned)) {
    return 'Account number must contain at least one digit';
  }

  if (/^0+$/.test(cleaned)) {
    return 'Account number cannot be all zeros';
  }

  return '';
};

export const validateAccountHolderName = (accountHolderName: string) => {
  // Check if accountHolderName is a non-empty string
  if (typeof accountHolderName !== 'string' || accountHolderName.trim().length === 0) {
    return 'Account holder name must be a non-empty string';
  }
  return '';
};

//valid cin number

export const validateCinNumber = (cinNumber: string) => {
  // must be number
  const cinNumberRegex = /^[0-9]+$/;
  return cinNumber.match(cinNumberRegex) ? '' : 'Enter valid cin number';
};

//iban number
export const validateIbanNumber = (ibanNumber: string) => {
  // Check if ibanNumber is a non-empty string
  if (typeof ibanNumber !== 'string' || ibanNumber.trim().length === 0) {
    return 'Iban number must be a non-empty string';
  }
  return '';
};

//swift code
export const validateSwiftCode = (swiftCode: string) => {
  const swiftCodeRegex = /^[0-9]+$/;
  return swiftCode.match(swiftCodeRegex) ? '' : 'Enter valid swift code';
  return '';
};

//branch name
export const validateBranchName = (branchName: string) => {
  // Check if branchName is a non-empty string
  if (typeof branchName !== 'string' || branchName.trim().length === 0) {
    return 'Branch name must be a non-empty string';
  }
  return '';
};

export const validateDate = (date?: Date) => {
  if (!date) {
    return 'Date is required';
  }

  const today = new Date();
  const birthDate = new Date(date);

  const age = today.getFullYear() - birthDate.getFullYear();

  if (age < 18 || (age === 18 && today.getMonth() < birthDate.getMonth())) {
    return 'You must be at least 18 years old';
  }

  return '';
};

//convert date to 20, Feb 2023 this formate
export const convertDate = (date: Date) => {
  const d = new Date(date);
  const month = d.toLocaleString('default', { month: 'short' });
  return `${d.getDate()}, ${month} ${d.getFullYear()}`;
};

//convert timestamp duration to min and sec
export const convertDuration = (duration: number) => {
  const min = Math.floor(duration / 60);
  const sec = duration % 60;
  return `${min}:${sec}`;
};

export const PersonalInfoPayload = (data: Partial<authTypes.PersonalInfo>) => {
  const payload = {
    first_name: data.first_name,
    last_name: data.last_name,
    country: data.country,
    birthday: data.birthday,
    national_identity_no: data.national_identity_no,
    stage_name: data.stage_name,
    description: data.description,
    profile_img: data.profile_img,
    profile_img_url: data.profile_img,
    username: data.username,
  };
  return payload;
};

export const BankInfoPayload = (data: Partial<artistTypes.Artist>) => {
  const payload = {
    bank_name: data.bank_name,
    acc_no: data.acc_no,
    acc_holder_name: data.acc_holder_name,
    cin_no: data.cin_no,
    iban: data.iban,
    swift_code: data.swift_code,
    branch_code: data.branch_code,
  };
  return payload;
};

export const validateName = (name: string) => {
  if (typeof name !== 'string' || name.trim().length === 0) {
    return 'Name must be a non-empty string';
  }
  return '';
};

export const validateUsernameOrEmail = (usernameOrEmail: string) => {
  if (typeof usernameOrEmail !== 'string' || usernameOrEmail.trim().length === 0) {
    return 'Username or email must be a non-empty string';
  }
  return '';
};
